import { FallbackError } from "@core/domain/errors/fallback.error";
import type { Nullable } from "@core/domain/types/nullable.type";
import { NotebookMessageDatasource } from "@notebook/data/datasources/notebook-message.datasource";
import type { NotebookMessageCreateError } from "@notebook/domain/errors/notebook-message-create.error";
import type { NotebookMessageFileCreateError } from "@notebook/domain/errors/notebook-message-file-create.error";
import type { NotebookMessageUpdateError } from "@notebook/domain/errors/notebook-message-update.error";
import type {
    NotebookMessage,
    NotebookMessageCreate,
    NotebookMessageFileCreate,
    NotebookMessages,
    NotebookMessageUpdate,
} from "@notebook/domain/models/notebook-message.model";
import type { NotebookMessageRepository } from "@notebook/domain/repositories/notebook-message.repository";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";

@injectable()
export class NotebookMessageImplRepository implements NotebookMessageRepository {
    constructor(
        @inject(NotebookMessageDatasource)
        private readonly notebookMessageDatasource: NotebookMessageDatasource,
    ) {}

    getAll(notebookId: string): ResultAsync<NotebookMessages, FallbackError> {
        return this.notebookMessageDatasource.fetchAll(notebookId);
    }

    fetchById(
        notebookMessageId: string,
        notebookId: string,
    ): ResultAsync<Nullable<NotebookMessage>, FallbackError> {
        return this.notebookMessageDatasource.fetchById(
            notebookMessageId,
            notebookId,
        );
    }

    create(
        notebookId: string,
        notebookMessage: NotebookMessageCreate,
    ): ResultAsync<NotebookMessage, NotebookMessageCreateError> {
        return this.notebookMessageDatasource.create(
            notebookId,
            notebookMessage,
        );
    }

    update(
        notebookMessageId: string,
        notebookId: string,
        notebookMessage: NotebookMessageUpdate,
    ): ResultAsync<NotebookMessage, NotebookMessageUpdateError> {
        return this.notebookMessageDatasource.update(
            notebookMessageId,
            notebookId,
            notebookMessage,
        );
    }

    delete(
        notebookId: string,
        notebookMessageId: string,
    ): ResultAsync<true, FallbackError> {
        return this.notebookMessageDatasource.delete(
            notebookId,
            notebookMessageId,
        );
    }

    createFile(
        notebookId: string,
        notebookMessageId: string,
        notebookMessageFile: NotebookMessageFileCreate,
    ): ResultAsync<boolean, NotebookMessageFileCreateError> {
        return this.notebookMessageDatasource.createFile(
            notebookId,
            notebookMessageId,
            notebookMessageFile,
        );
    }

    deleteFile(
        notebookId: string,
        notebookMessageId: string,
        fileId: string,
    ): ResultAsync<true, FallbackError> {
        return this.notebookMessageDatasource.deleteFile(
            notebookId,
            notebookMessageId,
            fileId,
        );
    }
}
